import { useDispatch, useSelector } from 'react-redux';
import {
  selectPlanList,
  selectUserActivePlanDetails,
  selectUserPlanList,
} from '../redux/slicers/subscriptionSlicer';
import { useEffect, useState } from 'react';
import {
  FREEMIUM_TYPE,
  OLD_SUBSCRIPTION_TYPE,
  PREMIUM_TYPE,
  useUserContext,
} from '../contexts/UserContext';
import { getDateTimeStampValue, getLocalStorage, setLocalStorage } from '../helpers/jsHelper';
import { CURRENT_USER } from '../constants/projectConstants';
import { setCurrentLoggedInInfo } from '../redux/slicers/activitySlicer';

// Mon Feb 10 2025 00:00:00 GMT+0530
// Show v1 UI after org created this below date.
const SHOW_V1_UI_DATE = 1739125800000;

export default function useCurrentOrganizationInfo() {
  const dispatch = useDispatch();
  const { currentUser, orgRegisteredOn } = useUserContext();
  const getUserPlans = useSelector(selectUserPlanList);
  const activePlan = useSelector(selectUserActivePlanDetails);
  const allMainPlanList = useSelector(selectPlanList);
  const [isSelfServePlan, setIsSelfServePlan] = useState(false);
  const [isFreemiumUser, setIsFreemium] = useState(false);
  const [isPremiumUser, setIsPremium] = useState(false);
  const [isUnlimitedTextures, setIsUnlimitedTextures] = useState(false);
  const [isAllowExpiredTextures, setIsAllowExpiredTextures] = useState(false);
  const [isOldSubscriptionUser, setIsOldSubscriptionUser] = useState(false);
  const [isOldPremiumUserWithNoPlan, setIsOldPremiumUserWithNoPlan] = useState(false);
  const [showV1UI, setShowV1UI] = useState(false);

  useEffect(async () => {
    if (orgRegisteredOn) {
      manageShowV1UI(orgRegisteredOn);
    }
  }, [getDateTimeStampValue(orgRegisteredOn)]);

  useEffect(() => {
    setIsOldPremiumUserWithNoPlan(isPremiumUser && getUserPlans?.length === 0);
  }, [isPremiumUser, getUserPlans]);

  useEffect(() => {
    if (currentUser) {
      setIsUnlimitedTextures(
        currentUser?.organization_detail?.is_unlimited_texture_upload || false
      );
      setIsAllowExpiredTextures(currentUser?.allow_expired_textures || currentUser?.organization_detail?.allow_expired_textures);
    } else {
      isUnlimitedTextures && setIsUnlimitedTextures(false);
      isAllowExpiredTextures && setIsAllowExpiredTextures(false);
    }
  }, [currentUser?.id, currentUser?.organization_detail]);

  useEffect(() => {
    let _isSelfServePlan = false;
    if (activePlan) {
      const activePrice = allMainPlanList
        ?.find((a) => +a.id === +activePlan?.plan_details?.[0]?.plan_id)
        ?.pricing_details?.find((b) => +b.id === +activePlan?.plan_details?.[0]?.price_id);
      _isSelfServePlan = activePrice?.is_self_serve_plan || activePlan?.is_upload_limit_plan;
      setIsSelfServePlan(_isSelfServePlan);
      setLocalStorage('isSelfServePlan', _isSelfServePlan);
    } else {
      _isSelfServePlan = false;
      setIsSelfServePlan(false);
      setLocalStorage('isSelfServePlan', false);
    }
    setIsFreemium(handleFreemiumUser());
    setIsPremium(handlePremiumUser());
    setIsOldSubscriptionUser(handleOldSubscriptionUser());

    dispatch(
      setCurrentLoggedInInfo({
        isSelfServePlan: _isSelfServePlan,
        isFreemiumUser: handleFreemiumUser(),
        isPremiumUser: handlePremiumUser(),
        isFreemiumOrSelfServeUser: handleFreemiumUser() || _isSelfServePlan,
        isPremiumAndSelfServePlan: handlePremiumUser() && _isSelfServePlan,
        isOldSubscriptionUser: handleOldSubscriptionUser(),
      })
    );
  }, [activePlan, allMainPlanList]);

  const manageShowV1UI = (date) => {
    if (date) {
      date = getDateTimeStampValue(date);
      setShowV1UI(date > SHOW_V1_UI_DATE ? true : false);
    } else {
      setShowV1UI(false);
    }
  };

  const getMyOrgType = () => {
    const userData = JSON.parse?.(getLocalStorage(CURRENT_USER) || '{}');
    return userData?.organization_detail?.subscription_type || null;
  };

  const handleFreemiumUser = () => {
    const type = getMyOrgType();
    if (!type) return false;
    const _type = type.toLowerCase();
    return FREEMIUM_TYPE.toLowerCase() === _type;
  };

  const handlePremiumUser = () => {
    const type = getMyOrgType();
    if (!type) return false;
    const _type = type.toLowerCase();
    return PREMIUM_TYPE.toLowerCase() === _type;
  };

  const handleOldSubscriptionUser = () => {
    const type = getMyOrgType();
    if (!type) return false;
    const _type = type.toLowerCase();
    return OLD_SUBSCRIPTION_TYPE.toLowerCase() === _type;
  };

  const isFreemiumOrSelfServeUser = isFreemiumUser || isSelfServePlan;
  const isPremiumAndSelfServePlan = isPremiumUser || isSelfServePlan;
  const hideNewPlans = isFreemiumUser && showV1UI; // this is added for freemium user whose org is created after 10th Feb 2025

  return {
    isSelfServePlan,
    isFreemiumUser,
    isPremiumUser,
    isFreemiumOrSelfServeUser,
    isPremiumAndSelfServePlan,
    isOldSubscriptionUser,
    isOldPremiumUserWithNoPlan,
    isUnlimitedTextures,
    isAllowExpiredTextures,
    hideNewPlans,
  };
}
